import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocumentPage from './DocumentPage';
import { useIntl as reactIntlUseIntl } from 'react-intl';
import { ApolloClient, useApolloClient as apolloUseApolloClient } from '@apollo/client';
import {
  getDocumentById as getDocumentByIdQuery,
  getDocumentByIdOfferQuery,
} from '../../../utils/api/get-document-by-id';
import { useDocumentWizardErrorNotifier } from '../shared/hooks/use-document-wizard-error-notifier/use-document-wizard-error-notifier.hook';
import { DocumentProvider, useDocumentState } from '../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../utils/document-wizard-utils/document-state-from-response';
import { useDeal } from '../../../contexts/DealContext';
import { getTeamsForUser } from '../../../utils/api/get-teams-for-user/get-teams-for-user';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { useDocumentTeamPermissions } from '../shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { useDocumentRole } from '../../../shared/hooks/useDocumentRole/useDocumentRole';
import { fillPlaceholders, paths } from '../../../routing';
import { useDocumentSave } from '../shared/hooks/use-document-save/use-document-save.hook';
import { updateDocument } from '../../../utils/api/update-document-offer/update-document';
import { useLeaseDocumentTitleSave } from '../shared/hooks/use-document-title-save/use-document-title-save.hook';
import LoadingSpinner from '../../../components/loading-spinner';
import { useModals } from '../../../contexts/ModalsContext';
import { DocumentStatusEnum } from '../../../shared/constants/document';
import { DocumentPayload } from '../../../utils/api/document-interface.types';
import { useAuth } from '../../../auth';
import { DocumentSourceEnum } from '../../../components/document-wizard/document-editor/constants/EditorConstats';
import {
  isAccessDenialError,
  rememberDocumentDestination,
  usePendingDealInvitationRecovery,
} from './usePendingDealInvitationRecovery';

type DocumentPageContainerProps = {
  useApolloClient?: (...args: any[]) => any;
  useIntl?: (...args: any[]) => any;
  getDocumentById?: (documentId: string | number) => (client: ApolloClient<object>) => Promise<DocumentPayload>;
};

const DocumentPageContainerInner = ({
  useIntl = reactIntlUseIntl,
  useApolloClient = apolloUseApolloClient,
  getDocumentById = getDocumentByIdQuery,
}: DocumentPageContainerProps) => {
  const apolloClient = useApolloClient();
  const reactIntl = useIntl();
  const navigate = useNavigate();
  const { documentId, teamId, dealId } = useParams();
  const { setCurrentDeal } = useDeal();
  const { closeDocumentModals } = useModals();
  const [authState] = useAuth();
  const { setOwnedTeams, setAllTeams } = usePermissions();
  const [documentGraphqlError, setDocumentGraphqlError] = useState(null);
  const { state: documentState, updateDocumentState, resetDocumentState } = useDocumentState();
  const document = documentState.document;
  const documentTitle = documentState.title;
  const isPending = documentState.isPending;
  const lastUpdated = documentState.lastUpdated;
  const notifyError = useDocumentWizardErrorNotifier();
  const receiverTeamId = documentState.receiverTeamId;
  const documentStatus = documentState.status;
  const { findRecoverableInvitationToken } = usePendingDealInvitationRecovery(dealId);
  // Set when the denial is recoverable (a pending invite covers this deal), so
  // the render path routes to acceptance instead of the generic error bounce.
  const [pendingInviteToken, setPendingInviteToken] = useState<string | null>(null);

  useEffect(
    function cleanUpOnUnmount() {
      return () => {
        resetDocumentState();
      };
    },
    [resetDocumentState]
  );

  const handleError = useCallback(() => {
    notifyError({
      message: reactIntl.formatMessage({
        id: 'lease-document-wizard.fetch-error',
        defaultMessage: 'Unable to fetch the Document',
      }),
      pinned: false,
    });
  }, [notifyError, reactIntl]);

  useEffect(() => {
    if (!documentId) {
      return;
    }

    // Always refetch to ensure fresh document data - force network-only to bypass cache
    apolloClient
      .query({
        query: getDocumentByIdOfferQuery,
        variables: { documentId },
        fetchPolicy: 'network-only',
      })
      .then(({ data }) => {
        const response = data.getDocumentById;
        setCurrentDeal(response.deal);
        updateDocumentState(documentStateFromResponse(response));
      })
      .catch((err) => {
        // Suppress false positive "Not Found" errors when document actually loads successfully
        if (err.message?.includes('Not Found') || err.message?.includes('Unhandled server error: Not Found')) {
          return;
        }

        // A counterparty who was emailed this document but has not yet accepted
        // the deal invitation is denied here — deal access rights are only
        // created on acceptance. That denial is RECOVERABLE: send them to the
        // invite they already hold instead of an error they can't act on.
        // No access is granted here; acceptance is what grants it, server-side.
        if (isAccessDenialError(err)) {
          const token = findRecoverableInvitationToken();
          if (token) {
            rememberDocumentDestination(window.location.pathname);
            setPendingInviteToken(token);
            setDocumentGraphqlError(err);
            return; // deliberately no error toaster — this is an actionable state
          }
        }

        setDocumentGraphqlError(err);
        handleError();
      });
  }, [apolloClient, updateDocumentState, handleError, documentId, findRecoverableInvitationToken]);

  useEffect(
    function fetchTeamsForUserOnMount() {
      getTeamsForUser()(apolloClient).then(({ ownedTeams, allTeams }) => {
        setOwnedTeams(ownedTeams);
        setAllTeams(allTeams);
      });
    },
    [apolloClient, setOwnedTeams, setAllTeams]
  );
  const permissions = useDocumentTeamPermissions({ apolloClient, teamId });
  const canInvite = permissions.hasInviteSendingPermission;
  const canEdit = permissions.hasEditPermission;
  const canComment = permissions.hasCommentPermission;
  const { isDocumentCreator, hasCurrentTeamPossession, isDocumentRoleLoading } = useDocumentRole(documentId!);
  const onClosePath = paths.deals.detail;
  const onCloseParams: { [key: string]: string } | {} = document && teamId ? { teamId, dealId } : {};
  const onCloseUrl = document && teamId ? fillPlaceholders(onClosePath, onCloseParams) : '';

  // Use backend's isEditable determination (handles approval requests + permitEditingDuringApproval logic)
  const isDocumentEditable = documentState.isEditable;
  const canEditDocument = permissions.hasEditPermission && isDocumentEditable;
  const canCommentDocument = permissions.hasCommentPermission;
  const documentSource = documentState.source;

  // Compute docAssetType from source and contentType (matches selectDocumentImportedAssetContentType selector logic)
  const docAssetType = !documentState.source
    ? null
    : documentState.source === DocumentSourceEnum.APP
      ? 'application/pdf'
      : documentState.contentType;
  const isDocXDocument = docAssetType !== 'application/pdf';

  const handleSaveError = useCallback(() => {
    notifyError({
      message: reactIntl.formatMessage({
        id: 'shared-offer-page.save-error',
        defaultMessage: 'Unable to save the Document',
      }),
      pinned: false,
    });
  }, [notifyError, reactIntl]);

  const handleSave = useDocumentSave({
    id: documentId!,
    save: updateDocument,
    onError: handleSaveError,
    apolloClient,
  });

  const saveTitle = useLeaseDocumentTitleSave({
    id: documentId!,
    save: updateDocument,
    onError: handleSaveError,
    apolloClient,
  });

  const handleTitleSave = useCallback(
    (title) => {
      if (isPending) {
        return;
      }

      saveTitle(title);
    },
    [isPending, saveTitle]
  );

  const handleLeaseWizardClose = useCallback(() => {
    navigate(onCloseUrl);
  }, [navigate, onCloseUrl]);

  const handleClose = async () => {
    handleLeaseWizardClose();
    resetDocumentState();
    closeDocumentModals();
  };

  // Redirect to deal page if document not found or no access
  if (!document && documentGraphqlError) {
    // Recoverable denial: route to the pending invitation. Accepting it creates
    // the deal access right, and the landing page then resumes to this document.
    if (pendingInviteToken && teamId) {
      navigate(
        fillPlaceholders(paths.deals.landingWithToken, { teamId, dealToken: pendingInviteToken }),
        { replace: true }
      );
      return <LoadingSpinner />;
    }
    if (teamId && dealId) {
      navigate(fillPlaceholders(paths.deals.detail, { teamId, dealId }), { replace: true });
    } else {
      navigate(paths.home, { replace: true });
    }
    return <LoadingSpinner />;
  }

  if (!document || !teamId || isDocumentRoleLoading || !documentSource || !dealId || !documentId) {
    return <LoadingSpinner />;
  }

  return (
    <DocumentPage
      canEdit={canEditDocument!}
      canComment={canCommentDocument}
      onSave={hasCurrentTeamPossession && (canEdit || canComment) && isDocumentEditable ? handleSave : () => {}}
      lastUpdated={lastUpdated}
      documentTitle={documentTitle || ''}
      onTitleSave={handleTitleSave}
      isDocumentCreator={isDocumentCreator}
      hasCurrentTeamPossession={hasCurrentTeamPossession!}
      document={document}
      documentId={documentId}
      teamId={teamId}
      dealId={dealId}
      canInvite={canInvite}
      permissions={permissions}
      isShared={!!receiverTeamId}
      documentStatus={(documentStatus as DocumentStatusEnum) || DocumentStatusEnum.DRAFT}
      handleClose={handleClose}
      documentSource={documentSource}
      isDocXDocument={isDocXDocument}
    />
  );
};

const DocumentPageContainer = (props: DocumentPageContainerProps) => (
  <DocumentProvider>
    <DocumentPageContainerInner {...props} />
  </DocumentProvider>
);

export default DocumentPageContainer;
